import { z } from 'zod';
import { EventEmitter } from 'events';
import { openSignalingChannel } from './common.js';
const WS_OPEN_STATE = 1;
const WS_CLOSING_STATE = 2;
const WS_CLOSED_STATE = 3;
const WS_CLOSE_NORMAL = 1000;
var MessageType;
(function (MessageType) {
    MessageType["IceCandidate"] = "iceCandidate";
    MessageType["Answer"] = "answer";
    MessageType["Offer"] = "offer";
    MessageType["Config"] = "config";
    MessageType["Broadcast"] = "broadcast";
})(MessageType || (MessageType = {}));
const RTCIceCandidateInitSchema = z.object({
    candidate: z.string().optional(),
    sdpMLineIndex: z.number().nullish(),
    sdpMid: z.string().nullish(),
    usernameFragment: z.string().nullish()
});
const MessageSchema = z.discriminatedUnion('type', [
    z
        .object({
        type: z.literal(MessageType.IceCandidate),
        candidate: RTCIceCandidateInitSchema
    })
        .passthrough(),
    z
        .object({
        type: z.literal(MessageType.Offer),
        sdp: z.string()
    })
        .passthrough(),
    z
        .object({
        type: z.literal(MessageType.Answer),
        sdp: z.string()
    })
        .passthrough(),
    z
        .object({
        type: z.literal(MessageType.Config),
        peerConnectionOptions: z
            .object({
            iceServers: z.array(z.object({
                username: z.string(),
                credential: z.string(),
                urls: z.array(z.string())
            }))
        })
            .passthrough()
    })
        .passthrough(),
    z.object({
        type: z.literal(MessageType.Broadcast),
        message: z.string()
    })
]);
class CirrusSignalingChannel extends EventEmitter {
    webSocket;
    rtcConfiguration;
    logger;
    signalingUrl;
    onMessageDuringOpen;
    createOnMessageDuringOpen(resolve) {
        return (event) => {
            try {
                const dataJson = JSON.parse(event.data);
                const message = MessageSchema.parse(dataJson);
                if (message.type === MessageType.Config) {
                    this.logger.debug('Cirrus: Got ICE servers via config message', {
                        iceServers: message.peerConnectionOptions.iceServers,
                        message
                    });
                    this.emit('open');
                    this.setupSignalingMessageHandlers();
                    resolve(message.peerConnectionOptions);
                }
                else {
                    const cirrusError = new Error('Did not receive config as first message from Cirrus');
                    this.emit('error', cirrusError);
                    resolve({ iceServers: [] });
                }
            }
            catch (error) {
                const handleError = new Error('Error while trying to handle Cirrus config message', { cause: error });
                this.emit('error', handleError);
                resolve({ iceServers: [] });
            }
        };
    }
    constructor(props) {
        super();
        this.logger = props.logger;
        this.signalingUrl = props.signalingUrl;
        this.rtcConfiguration = new Promise((resolve) => {
            this.onMessageDuringOpen = this.createOnMessageDuringOpen(resolve);
        });
    }
    open() {
        if (!this.onMessageDuringOpen) {
            throw new Error('The message handler for the initial Cirrus message has not been setup correctly in the constructor. Cannot open the sig');
        }
        const webSocket = new WebSocket(this.signalingUrl);
        webSocket.onclose = (closeEvent) => {
            this.emit('close', closeEvent);
        };
        webSocket.onmessage = (e) => this.onMessageDuringOpen?.(e);
        this.webSocket = webSocket;
    }
    close() {
        this.webSocket?.close(WS_CLOSE_NORMAL, 'user requested close');
    }
    closeAsync() {
        if (!this.webSocket || this.webSocket.readyState === WS_CLOSED_STATE)
            return Promise.resolve();
        if ([WS_OPEN_STATE, WS_CLOSING_STATE].includes(this.webSocket.readyState)) {
            return new Promise((resolve, reject) => {
                if (!this.webSocket) {
                    resolve();
                    return;
                }
                setTimeout(() => reject(new Error('Cirrus Websocket did not close in time')), 5000);
                this.webSocket.onclose = () => {
                    this.emit('close');
                    this.logger.debug('Signaling Websocket closed', { readyState: this.webSocket?.readyState });
                    resolve();
                };
                this.close();
            }).finally(() => {
                if (this.webSocket)
                    this.webSocket.onclose = null;
            });
        }
        this.close();
        return Promise.resolve();
    }
    send(request) {
        if (this.webSocket?.readyState === WS_OPEN_STATE) {
            this.webSocket.send(JSON.stringify(request));
        }
    }
    sendIceCandidate(candidate) {
        this.send({ type: MessageType.IceCandidate, candidate });
    }
    sendSdpOffer(rtcSessionDescription) {
        this.send(rtcSessionDescription);
    }
    sendSdpAnswer(rtcSessionDescription) {
        this.send(rtcSessionDescription);
    }
    async getRTCConfiguration() {
        return this.rtcConfiguration;
    }
    setupSignalingMessageHandlers() {
        if (!this.webSocket) {
            throw new Error('Trying to setup message handlers failed after receiving the first message on a Cirrus websocket signaling channel, because the websocket is gone.');
        }
        this.webSocket.onmessage = (event) => {
            const dataJson = JSON.parse(event.data);
            const messageParsed = MessageSchema.safeParse(dataJson);
            if (messageParsed.success) {
                const message = messageParsed.data;
                switch (message.type) {
                    case MessageType.Offer:
                        this.emit('sdpOffer', message);
                        break;
                    case MessageType.Answer:
                        this.emit('sdpAnswer', message);
                        break;
                    case MessageType.IceCandidate:
                        this.emit('iceCandidate', message.candidate);
                        break;
                    case MessageType.Broadcast:
                        this.emit('broadcast', message.message);
                        break;
                }
            }
        };
    }
}
export async function openCirrusSignaling(props) {
    const signalingUrl = props.cirrusSignalingOptions.signalingUrl;
    const logger = props.logger;
    logger.debug('Cirrus: Creating signaling client');
    const signalingClient = new CirrusSignalingChannel({ logger, signalingUrl });
    return openSignalingChannel(signalingClient, logger);
}
//# sourceMappingURL=cirrus.js.map